/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { kebabCase, sortBy, chunk } from 'lodash';
import {
  chakra,
  Select,
  Container,
  Box,
  Flex,
  Text,
  GridItem,
} from '@chakra-ui/react';
import { navigate } from 'gatsby';
import { useEffectOnce } from 'react-use';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import Pagination from '../../../../tailwind/Pagination';
import PressCard from '@repo/ui-components/dept/molecules/PressCard';
import { getPageId } from '../../../../../utils/pagination';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import PressReleases from '../../../../../../sites/petsafe/src/content/pressReleases.json';

type Sorter = {
  title: string;
  reverse?: boolean;
  sortProp: string;
};

export type Sorters = {
  [key: string]: Sorter;
};

interface Block {
  _type: string;
  _key: string;
}

interface PressReleasesProps {
  title: string;
  language: string;
  _id: string;
  slug: {
    current: string;
  };
  publishedDate: string;
  description: string;
}

interface Location {
  pathname: string;
  search: string;
}

interface GridPressProps {
  block: Block;
  location: Location;
}

export default function GridPress({ block, location }: GridPressProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { _type, _key } = block;
  const [chunks, setChunks] = useState<PressReleasesProps[][]>([]);
  const [sort, setSort] = useState<string>('');
  const { tt, locale } = useUiComponentsInternationalContext();

  const items = PressReleases.filter(
    pressRelease => pressRelease.language === locale
  );
  const searchParams = new URLSearchParams(location.search);
  const pageId = getPageId(location);
  const pageElements = chunks[parseInt(pageId + 1, 10) - 1 || 0] || [];

  const SORTERS: Sorters = useMemo(
    () => ({
      newest: {
        title: tt(s => s.blocksDeptComponents.newestLabel),
        reverse: true,
        sortProp: 'publishedDate',
      },
      oldest: {
        title: tt(s => s.blocksDeptComponents.oldestLabel),
        sortProp: 'publishedDate',
      },
      atoz: {
        title: tt(s => s.blocksDeptComponents.aToZLabel),
        sortProp: 'title',
      },
      ztoa: {
        title: tt(s => s.blocksDeptComponents.zToALabel),
        reverse: true,
        sortProp: 'title',
      },
    }),
    [tt]
  );

  const getPressChunks = (): PressReleasesProps[][] => {
    const chunkSize = 6;

    const sortParam = searchParams.get('sort');
    const isSorted = Object.keys(SORTERS).includes(sortParam);

    if (isSorted) {
      const sortedItems = sortBy(items, SORTERS[sortParam].sortProp);

      if (SORTERS[sortParam].reverse) {
        return chunk(sortedItems.reverse(), chunkSize);
      }
      return chunk(sortedItems, chunkSize);
    }

    return chunk(items, chunkSize);
  };

  useEffectOnce(() => {
    setChunks(getPressChunks());
  });

  useEffect(() => {
    const newChunks = getPressChunks();
    setChunks(newChunks);
  }, [sort]);

  useEffect(() => {
    const sortParam = searchParams?.get('sort');

    setSort(sortParam || '');
  }, [location]);

  const handleSort = ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
    if (!target.value) {
      searchParams.delete('sort');
    } else {
      searchParams.set('sort', target.value);
    }

    const newSearch = `?${searchParams.toString()}`;

    if (newSearch !== location.search) {
      navigate(newSearch);
    }
  };

  return (
    <Container as="section" id={`${kebabCase(_type)}_${_key}`} py={10}>
      <Flex
        mx={{ base: '-24px', lg: 0 }}
        justify="space-between"
        align="center"
        py="2"
        pl="6"
        pr={{ base: 6, lg: 2 }}
        bg={{ base: 'neutral.light', lg: 'primary.100' }}
        pos="relative"
        borderRadius={{ base: 'none', lg: 'lg' }}
        mb={6}
        gap={2}
        borderBottom={{ base: '1px solid', lg: 'none' }}
        borderColor="stroke.base"
      >
        <Text
          textStyle={{ base: 'bodyLarge', lg: 'h4' }}
          as="h4"
          pos="relative"
          fontWeight={{ lg: 'bold' }}
          w={{ base: 'full', lg: 'max' }}
        >
          {tt(s => s.blocksDeptComponents.gridPressResultsLabel, {
            count: chunks.flatMap(c => [...c]).length.toString(),
          })}
        </Text>
        <Select
          w={{ base: 'full', lg: 'max' }}
          placeholder={tt(s => s.searchCommon.sortByLabel)}
          onChange={handleSort}
          value={sort}
          bg="white"
          textStyle="body"
          fontWeight="light"
        >
          {Object.entries(SORTERS).map(([value, { title }]) => (
            <chakra.option
              key={value}
              value={value}
              textStyle="body"
              fontWeight="light"
            >
              {title}
            </chakra.option>
          ))}
        </Select>
      </Flex>

      <DeptGrid>
        {pageElements.map(p => (
          <GridItem colSpan={{ base: 6, xl: 4 }} key={p._id}>
            <PressCard
              title={p.title}
              date={p.publishedDate}
              description={p.description}
              link={p.slug.current}
            />
          </GridItem>
        ))}
      </DeptGrid>
      <Box p={6}>
        <Pagination numOfPages={chunks.length} page={pageId} />
      </Box>
    </Container>
  );
}
